// @ts-nocheck
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { getSharedStartDate, isBaseAmountDataCompleteForIndividuals } from '../../../../utils/compensationStep';
import {
  PageHeader,
  Card,
  Button,
  InfoBanner,
  Th,
  Td,
  WorkflowDateField,
} from '../../components/WorkflowFormPrimitives';
import { fmt } from '../../utils/formatCurrency';
import { useCompanyContext } from '../../context/CompanyContext';
import {
  formatIsoYmdForDisplay,
  getIsoYmdCalendarYear,
  parseLocalDateFromIsoYmd,
} from '../../../../utils/isoDateDisplay';

export const BaseAmount = ({ formData, setFormData, setActiveTab, continueMode }) => {
  const { isCurrentAnalysisLocked } = useCompanyContext();
  const individuals = formData.individuals || [];
  const uniqueNames = Array.from(new Set(individuals.map((i: { name: string }) => i.name)));
  const uniqueIndividuals = uniqueNames.map((name) => individuals.find((i) => i.name === name));
  const data = formData.baseAmountData || {};
  const closingYear = getIsoYmdCalendarYear(formData.closingDate) ?? new Date().getFullYear();
  const years = [closingYear - 5, closingYear - 4, closingYear - 3, closingYear - 2, closingYear - 1];
  const update = (id, field, v) =>
    setFormData((p) => ({
      ...p,
      baseAmountData: { ...(p.baseAmountData || {}), [id]: { ...((p.baseAmountData || {})[id] || {}), [field]: v } },
    }));

  const updateSharedStartDate = (id, v) =>
    setFormData((p) => ({
      ...p,
      compensation: {
        ...(p.compensation || {}),
        [id]: { ...((p.compensation || {})[id] || {}), startDate: v },
      },
      baseAmountData: {
        ...(p.baseAmountData || {}),
        [id]: { ...((p.baseAmountData || {})[id] || {}), startDate: v },
      },
    }));

  const getAnnualizationDetails = (id, year) => {
    const d = data[id] || {};
    const rawComp = +d[year] || 0;
    const startDateStr = getSharedStartDate(formData, id);
    const startDate = parseLocalDateFromIsoYmd(startDateStr);
    const specialPayments = +d.specialPayments || 0;
    if (!startDate || !rawComp) return { raw: rawComp, annualized: rawComp, daysWorked: 365, isPartialYear: false };
    const startYear = startDate.getFullYear();
    if (startYear === year && year >= years[0] && year < closingYear) {
      const yearEnd = new Date(year, 11, 31);
      const daysWorked = Math.floor((yearEnd.getTime() - startDate.getTime()) / 864e5) + 1;
      if (daysWorked < 365 && daysWorked > 0) {
        const amountToAnnualize = Math.max(0, rawComp - specialPayments);
        const annualizedBase = amountToAnnualize * (365 / daysWorked);
        return {
          raw: rawComp,
          annualized: Math.round(annualizedBase + specialPayments),
          daysWorked,
          isPartialYear: true,
          specialPayments,
        };
      }
    }
    return { raw: rawComp, annualized: rawComp, daysWorked: 365, isPartialYear: false };
  };

  const calcBase = (id) => {
    const startDateStr = getSharedStartDate(formData, id);
    const startDate = parseLocalDateFromIsoYmd(startDateStr);
    const startYear = startDate ? startDate.getFullYear() : 0;
    if (startYear === closingYear) {
      const comp = formData.compensation?.[id] || {};
      return +comp.baseSalary || 0;
    }
    const applicableYears = years.filter((y) => (!startDate || y >= startYear) && y < closingYear);
    const vals = applicableYears.map((y) => getAnnualizationDetails(id, y).annualized).filter((v) => v > 0);
    return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
  };

  const calcThreshold = (base) => Math.max(0, base * 3 - 1);

  const canContinueBaseAmount =
    uniqueIndividuals.length > 0 && isBaseAmountDataCompleteForIndividuals(uniqueIndividuals, formData);
  const forwardPrimaryLabel = isCurrentAnalysisLocked ? 'Continue' : 'Save and continue';

  return (
    <div className="space-y-5">
      <PageHeader
        title="Base Amount"
        subtitle={`5-Year W-2 Box 1 History and Base Amount Calculation (Years Prior to ${closingYear} Closing)`}
      />
      {uniqueIndividuals.length === 0 ? (
        <InfoBanner variant="warning" showIcon>
          Please add disqualified individuals first.
        </InfoBanner>
      ) : (
        <>
          <Card padding={false}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <Th>Name</Th>
                    <Th>Start Date</Th>
                    {years.map((y) => (
                      <Th key={y}>{y}</Th>
                    ))}
                    <Th>Special Pmts</Th>
                    <Th className="bg-emerald-50/50">Base Amt</Th>
                    <Th className="bg-emerald-50/50">280G Limit</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {uniqueIndividuals.map((ind) => {
                    const d = data[ind.id] || {};
                    const startDateStrRow = getSharedStartDate(formData, ind.id);
                    const startDate = parseLocalDateFromIsoYmd(startDateStrRow);
                    const startYear = startDate ? startDate.getFullYear() : 0;
                    const base = calcBase(ind.id),
                      threshold = calcThreshold(base);
                    const usesCurrentSalary = startYear === closingYear;
                    return (
                      <tr key={ind.id} className="hover:bg-gray-50/50">
                        <Td highlight>
                          <div>
                            <span className="text-gray-900">{ind.name}</span>
                            <br />
                            <span className="text-xs text-gray-400">{ind.title}</span>
                          </div>
                        </Td>
                        <Td className="align-top">
                          {startDateStrRow ? (
                            <div className="min-w-[10rem] py-0.5">
                              <div className="text-xs text-gray-900">{formatIsoYmdForDisplay(startDateStrRow)}</div>
                              <p className="text-xs text-gray-500 mt-1 m-0">
                                Same as Compensation
                                {!isCurrentAnalysisLocked ? (
                                  <>
                                    {' '}
                                    —{' '}
                                    <button
                                      type="button"
                                      className="text-emerald-700 hover:text-emerald-900 underline font-medium"
                                      onClick={() => setActiveTab('compensation')}
                                    >
                                      change there
                                    </button>
                                  </>
                                ) : null}
                              </p>
                            </div>
                          ) : (
                            <WorkflowDateField
                              compact
                              variant="amber"
                              value={startDateStrRow}
                              onChange={(v) => updateSharedStartDate(ind.id, v)}
                            />
                          )}
                        </Td>
                        {years.map((yr) => {
                          const isBeforeStart = startDate && yr < startYear;
                          const details = getAnnualizationDetails(ind.id, yr);
                          return (
                            <Td
                              key={yr}
                              className={isBeforeStart ? 'bg-gray-50' : details.isPartialYear ? 'bg-amber-50/50' : ''}
                            >
                              {isBeforeStart ? (
                                <span className="text-gray-300 text-xs">N/A</span>
                              ) : (
                                <div>
                                  <input
                                    type="number"
                                    value={d[yr] || ''}
                                    onChange={(e) => update(ind.id, yr, e.target.value)}
                                    className={`w-20 h-8 px-2 text-xs border rounded-md ${details.isPartialYear ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`}
                                    placeholder="0"
                                  />
                                  {details.isPartialYear && details.raw > 0 && (
                                    <div className="text-xs text-emerald-700 mt-0.5">→ {fmt(details.annualized)}</div>
                                  )}
                                </div>
                              )}
                            </Td>
                          );
                        })}
                        <Td>
                          {startYear >= years[0] && startYear < closingYear ? (
                            <input
                              type="number"
                              value={d.specialPayments || ''}
                              onChange={(e) => update(ind.id, 'specialPayments', e.target.value)}
                              className="w-20 h-8 px-2 text-xs border border-amber-300 bg-amber-50 rounded-md"
                              placeholder="0"
                            />
                          ) : (
                            <span className="text-gray-300 text-xs">N/A</span>
                          )}
                        </Td>
                        <Td className="bg-emerald-50/30 font-medium text-emerald-700">
                          {usesCurrentSalary && <div className="text-xs text-gray-400 font-normal">Curr. Salary</div>}
                          {fmt(base)}
                        </Td>
                        <Td className="bg-emerald-50/30 font-medium text-emerald-700">{fmt(threshold)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Annualization detail table: only when at least one individual has an annualized year */}
          {(() => {
            const annualizedRows: {
              ind: { id: string; name: string; title?: string };
              year: number;
              details: ReturnType<typeof getAnnualizationDetails>;
            }[] = [];
            uniqueIndividuals.forEach((ind) => {
              years.forEach((yr) => {
                const details = getAnnualizationDetails(ind.id, yr);
                if (details.isPartialYear && details.raw > 0) {
                  annualizedRows.push({ ind, year: yr, details });
                }
              });
            });
            if (annualizedRows.length === 0) return null;
            return (
              <Card padding={false}>
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-xs font-medium text-gray-800">How annualized amounts were determined</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Partial year: (Box 1 − Special Payments) × (365 ÷ Days Worked) + Special Payments
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <Th>Name</Th>
                        <Th>Year</Th>
                        <Th className="text-right">Box 1 (W-2)</Th>
                        <Th className="text-right">Special Payments</Th>
                        <Th className="text-right">Amount to Annualize</Th>
                        <Th className="text-right">Days Worked</Th>
                        <Th className="text-right">365 ÷ Days</Th>
                        <Th className="text-right bg-emerald-50/50">Annualized Amount</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {annualizedRows.map(({ ind, year, details }) => {
                        const amountToAnnualize = Math.max(0, details.raw - (details.specialPayments ?? 0));
                        const multiplier = details.daysWorked > 0 ? 365 / details.daysWorked : 0;
                        const annualizedBase = amountToAnnualize * multiplier;
                        const total = Math.round(annualizedBase + (details.specialPayments ?? 0));
                        return (
                          <tr key={`${ind.id}-${year}`} className="hover:bg-gray-50/50">
                            <Td highlight>
                              <div>
                                <span className="text-gray-900">{ind.name}</span>
                                <br />
                                <span className="text-xs text-gray-400">{ind.title}</span>
                              </div>
                            </Td>
                            <Td>{year}</Td>
                            <Td className="text-right">{fmt(details.raw)}</Td>
                            <Td className="text-right">{fmt(details.specialPayments ?? 0)}</Td>
                            <Td className="text-right">{fmt(amountToAnnualize)}</Td>
                            <Td className="text-right">{details.daysWorked}</Td>
                            <Td className="text-right">{multiplier > 0 ? multiplier.toFixed(4) : '—'}</Td>
                            <Td className="text-right bg-emerald-50/30 font-medium text-emerald-700">{fmt(total)}</Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })()}
        </>
      )}

      <InfoBanner showIcon>
        <div className="space-y-2.5">
          <p className="m-0">
            <strong>Note:</strong> After you enter <strong>start date</strong> on Compensation, it appears here
            automatically — you do not enter it again. If you jump to this step first, use the date control in the table
            to set it once; it will show on Compensation too.
          </p>
          <p className="m-0 pt-2.5 border-t border-emerald-200/80">
            <strong>Calculation rules:</strong> Base Amount = Average of applicable years. Partial Year = (Box 1 −
            Special Payments) × (365 ÷ Days Worked) + Special Payments. 280G Threshold = (3 × Base Amount) − $1.
          </p>
        </div>
      </InfoBanner>
      <div className="flex justify-between gap-3">
        <Button variant="secondary" onClick={() => setActiveTab('compensation')}>
          Previous
        </Button>
        {continueMode === 'dual' ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={!canContinueBaseAmount}
              onClick={() => setActiveTab('parachute', { persist: false })}
            >
              Continue <ChevronRight size={14} className="ml-1.5" />
            </Button>
          </div>
        ) : (
          <Button disabled={!canContinueBaseAmount} onClick={() => setActiveTab('parachute', { persist: true })}>
            {forwardPrimaryLabel} <ChevronRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
